import React, { useEffect, useMemo, useState } from "react";
import CommonFooter from "../../core/common/footer/commonFooter";
import { all_routes } from "../../Router/all_routes";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import { DatePicker, message } from "antd";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import Table from "../../core/pagination/datatable";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
  getStockHistoryFilters,
  getStockHistoryReport,
  getStockHistorySummary,
  downloadStockHistoryExcel,
  downloadStockHistoryPdf,
} from "../../api/stockHistoryApi";

const DEFAULT_PRODUCT_IMAGE = "/assets/img/by-default.png";
const IMAGE_BASE = (
  process.env.REACT_APP_API_IMAGE_BASE_URL ||
  "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

const isAbsoluteHttp = (url = "") => /^https?:\/\//i.test(url);
const isDataUrl = (url = "") => /^data:image/i.test(url);

const resolveImageUrl = (raw) => {
  if (!raw) return DEFAULT_PRODUCT_IMAGE;

  const baseVal = Array.isArray(raw) ? raw[0] : raw;
  const val =
    typeof baseVal === "object" && baseVal !== null
      ? baseVal.url ||
        baseVal.imageUrl ||
        baseVal.key ||
        baseVal.path ||
        baseVal.location ||
        baseVal.src ||
        ""
      : baseVal;

  if (!val || typeof val !== "string") return DEFAULT_PRODUCT_IMAGE;
  if (isDataUrl(val) || isAbsoluteHttp(val)) return val;

  const key = val.replace(/^\/+/, "");
  if (!key) return DEFAULT_PRODUCT_IMAGE;
  return `${IMAGE_BASE}/${key}`;
};

const cardBaseStyle = {
  border: "0",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 10px 30px rgba(17, 24, 39, 0.08)",
};

const actionBtnBase = {
  minWidth: "132px",
  height: "42px",
  borderRadius: "10px",
  fontWeight: 600,
  fontSize: "14px",
};

const reactSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 46,
    borderRadius: 10,
    borderColor: "#dee2e6",
    boxShadow: "none",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 12px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 46,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
};

const Stockhistory = () => {
  const route = all_routes;
  const t = (key) => (window.translate ? window.translate(key) : key);

  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const [tableData, setTableData] = useState([]);
  const [summary, setSummary] = useState({
    totalRows: 0,
    totalInitialQuantity: 0,
    totalAddedQuantity: 0,
    totalSoldQuantity: 0,
    totalDefectiveQuantity: 0,
    totalFinalQuantity: 0,
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 1,
  });

  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    products: [],
    brands: [],
    units: [],
    warehouses: [],
    stores: [],
  });

  const [filters, setFilters] = useState({
    date: null,
    category: null,
    product: null,
  });

  const categoryOptions = useMemo(() => {
    return (filterOptions.categories || []).map((item) => ({
      value: item?._id,
      label:
        item?.name?.[localStorage.getItem("lang") || "en"] ||
        item?.name?.en ||
        item?.name?.ar ||
        t("N/A"),
    }));
  }, [filterOptions.categories]);

  const productOptions = useMemo(() => {
    return (filterOptions.products || []).map((item) => ({
      value: item?._id,
      label:
        item?.productName?.[localStorage.getItem("lang") || "en"] ||
        item?.productName?.en ||
        item?.productName?.ar ||
        item?.sku ||
        t("N/A"),
    }));
  }, [filterOptions.products]);

  const buildParams = (customFilters = filters, extra = {}) => ({
    page: extra.page || 1,
    limit: extra.limit || 100,
    date: customFilters?.date ? customFilters.date.format("YYYY-MM-DD") : "",
    category: customFilters?.category?.value || "",
    product: customFilters?.product?.value || "",
  });

  const fetchFilters = async () => {
    try {
      setFilterLoading(true);
      const response = await getStockHistoryFilters();
      setFilterOptions(response?.data || {});
    } catch (error) {
      message.error(error?.message || t("Failed to load stock history filters"));
    } finally {
      setFilterLoading(false);
    }
  };

  const fetchStockHistory = async (customFilters = filters) => {
    try {
      setLoading(true);

      const params = buildParams(customFilters, { page: 1, limit: 100 });

      const [reportResponse, summaryResponse] = await Promise.all([
        getStockHistoryReport(params),
        getStockHistorySummary(params),
      ]);

      const rows = Array.isArray(reportResponse?.data) ? reportResponse.data : [];

      const formattedData =
        rows.length > 0
          ? rows.map((item, index) => ({
              key: item?._id || `${item?.productId || "row"}-${index}`,
              SKU: item?.sku || t("N/A"),
              Product_Name: item?.productName || t("N/A"),
              Initial_Quantity: Number(item?.initialQuantity || 0),
              Added_Quantity: Number(item?.addedQuantity || 0),
              Sold_Quantity: Number(item?.soldQuantity || 0),
              Defective_Quantity: Number(item?.defectiveQuantity || 0),
              Final_Quantity: Number(item?.finalQuantity || 0),
              img: resolveImageUrl(
                item?.image || item?.images || item?.productImage || item?.raw?.image
              ),
              raw: item,
            }))
          : [];

      setTableData(formattedData);
      setPagination(
        reportResponse?.pagination || {
          total: formattedData.length,
          page: 1,
          limit: 100,
          totalPages: 1,
        }
      );
      setSummary(
        summaryResponse?.data || reportResponse?.summary || {
          totalRows: 0,
          totalInitialQuantity: 0,
          totalAddedQuantity: 0,
          totalSoldQuantity: 0,
          totalDefectiveQuantity: 0,
          totalFinalQuantity: 0,
        }
      );
    } catch (error) {
      console.error("Stock history fetch error:", error);
      message.error(error?.message || t("Failed to fetch stock history report"));
      setTableData([]);
      setSummary({
        totalRows: 0,
        totalInitialQuantity: 0,
        totalAddedQuantity: 0,
        totalSoldQuantity: 0,
        totalDefectiveQuantity: 0,
        totalFinalQuantity: 0,
      });
      setPagination({
        total: 0,
        page: 1,
        limit: 50,
        totalPages: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilters();
    fetchStockHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateReport = async (e) => {
    e.preventDefault();
    await fetchStockHistory(filters);
  };

  const handleRefresh = async (e) => {
    e.preventDefault();
    await fetchFilters();
    await fetchStockHistory(filters);
  };

  const handleDownloadExcel = async () => {
    try {
      setExportLoading(true);
      await downloadStockHistoryExcel(buildParams(filters, { page: 1, limit: 1000 }));
      message.success(t("Stock history excel downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download stock history excel"));
    } finally {
      setExportLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setExportLoading(true);
      await downloadStockHistoryPdf(buildParams(filters, { page: 1, limit: 1000 }));
      message.success(t("Stock history pdf downloaded successfully"));
    } catch (error) {
      message.error(error?.message || t("Failed to download stock history pdf"));
    } finally {
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: t("SKU"),
      dataIndex: "SKU",
      render: (text) => <span className="fw-medium">{text || t("N/A")}</span>,
      sorter: (a, b) => (a?.SKU || "").localeCompare(b?.SKU || ""),
    },
    {
      title: t("Product Name"),
      dataIndex: "Product_Name",
      render: (text, record) => (
        <div className="d-flex align-items-center" style={{ minWidth: 220 }}>
          <div
            className="me-2 d-flex align-items-center justify-content-center"
            style={{
              width: 42,
              height: 42,
              minWidth: 42,
              borderRadius: 10,
              background: "#f8f9fa",
              overflow: "hidden",
              border: "1px solid #eef1f4",
            }}
          >
            <img
              src={record?.img || DEFAULT_PRODUCT_IMAGE}
              className="img-fluid"
              alt={t("Product")}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
              }}
            />
          </div>
          <div>
            <p
              className="text-dark mb-0 fw-medium"
              style={{ lineHeight: "1.35", wordBreak: "break-word" }}
            >
              {text || t("N/A")}
            </p>
          </div>
        </div>
      ),
      sorter: (a, b) => (a?.Product_Name || "").localeCompare(b?.Product_Name || ""),
    },
    {
      title: t("Initial Quantity"),
      dataIndex: "Initial_Quantity",
      render: (text) => <span>{Number(text || 0)}</span>,
      sorter: (a, b) =>
        Number(a?.Initial_Quantity || 0) - Number(b?.Initial_Quantity || 0),
    },
    {
      title: t("Added Quantity"),
      dataIndex: "Added_Quantity",
      render: (text) => <span>{Number(text || 0)}</span>,
      sorter: (a, b) =>
        Number(a?.Added_Quantity || 0) - Number(b?.Added_Quantity || 0),
    },
    {
      title: t("Sold Quantity"),
      dataIndex: "Sold_Quantity",
      render: (text) => <span>{Number(text || 0)}</span>,
      sorter: (a, b) =>
        Number(a?.Sold_Quantity || 0) - Number(b?.Sold_Quantity || 0),
    },
    {
      title: t("Defective Quantity"),
      dataIndex: "Defective_Quantity",
      render: (text) => <span>{Number(text || 0)}</span>,
      sorter: (a, b) =>
        Number(a?.Defective_Quantity || 0) - Number(b?.Defective_Quantity || 0),
    },
    {
      title: t("Final Quantity"),
      dataIndex: "Final_Quantity",
      render: (text) => <span className="fw-semibold">{Number(text || 0)}</span>,
      sorter: (a, b) =>
        Number(a?.Final_Quantity || 0) - Number(b?.Final_Quantity || 0),
    },
  ];

  const statCards = [
    {
      title: t("Total Rows"),
      value: summary?.totalRows || tableData.length || 0,
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      icon: "ti ti-layout-grid",
    },
    {
      title: t("Initial Qty"),
      value: summary?.totalInitialQuantity || 0,
      gradient: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
      icon: "ti ti-box",
    },
    {
      title: t("Added Qty"),
      value: summary?.totalAddedQuantity || 0,
      gradient: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
      icon: "ti ti-plus",
    },
    {
      title: t("Final Qty"),
      value: summary?.totalFinalQuantity || 0,
      gradient: "linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)",
      icon: "ti ti-package",
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content" style={{ paddingBottom: 24 }}>
          <div className="table-tab mb-4">
            <ul
              className="nav nav-pills"
              style={{ gap: 10, flexWrap: "wrap" }}
            >
              <li className="nav-item">
                <Link className="nav-link" to={route.inventoryreport}>
                  {t("Inventory Report")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link active" to={route.stockhistory}>
                  {t("Stock History")}
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to={route.soldstock}>
                  {t("Sold Stock")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="page-header mb-4">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4 className="mb-1">{t("Stock History")}</h4>
                <h6 className="mb-0">{t("View Reports of Stock History")}</h6>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <button
                type="button"
                className="btn"
                onClick={handleDownloadExcel}
                disabled={exportLoading}
                style={{
                  ...actionBtnBase,
                  border: "1px solid #0d6efd",
                  color: "#0d6efd",
                  background: "#fff",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export Excel")}
              </button>

              <button
                type="button"
                className="btn"
                onClick={handleDownloadPdf}
                disabled={exportLoading}
                style={{
                  ...actionBtnBase,
                  border: "1px solid #198754",
                  color: "#198754",
                  background: "#fff",
                }}
              >
                {exportLoading ? t("Please wait...") : t("Export PDF")}
              </button>

              <ul className="table-top-head" style={{ marginBottom: 0 }}>
                <li>
                  <Link to="#" onClick={handleRefresh}>
                    <RefreshIcon />
                  </Link>
                </li>
                <CollapesIcon />
              </ul>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4" style={cardBaseStyle}>
            <div className="card-body" style={{ padding: "22px 22px 8px" }}>
              <form onSubmit={handleGenerateReport}>
                <div className="row g-3 align-items-end">
                  <div className="col-xl-3 col-md-6">
                    <label className="form-label fw-medium mb-2">{t("Choose Date")}</label>
                    <div className="input-icon-start position-relative">
                      <DatePicker
                        className="form-control datetimepicker"
                        placeholder={t("dd/mm/yyyy")}
                        value={filters.date}
                        onChange={(date) =>
                          setFilters((prev) => ({
                            ...prev,
                            date,
                          }))
                        }
                        format="DD/MM/YYYY"
                        style={{
                          width: "100%",
                          height: 46,
                          borderRadius: 10,
                        }}
                      />
                      <span className="input-icon-left">
                        <i className="ti ti-calendar" />
                      </span>
                    </div>
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <label className="form-label fw-medium mb-2">
                      {t("Category")}
                    </label>
                    <Select
                      classNamePrefix="react-select"
                      options={categoryOptions}
                      placeholder={t("Choose")}
                      value={filters.category}
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          category: selected,
                        }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                    />
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <label className="form-label fw-medium mb-2">{t("Products")}</label>
                    <Select
                      classNamePrefix="react-select"
                      options={productOptions}
                      placeholder={t("Choose")}
                      value={filters.product}
                      onChange={(selected) =>
                        setFilters((prev) => ({
                          ...prev,
                          product: selected,
                        }))
                      }
                      isClearable
                      isLoading={filterLoading}
                      styles={reactSelectStyles}
                    />
                  </div>

                  <div className="col-xl-3 col-md-6">
                    <button
                      className="btn w-100"
                      type="submit"
                      disabled={loading}
                      style={{
                        height: 46,
                        borderRadius: 10,
                        background: "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)",
                        border: "none",
                        color: "#fff",
                        fontWeight: 700,
                        boxShadow: "0 8px 18px rgba(247,151,30,0.25)",
                      }}
                    >
                      {loading ? t("Please wait...") : t("Generate Report")}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {statCards.map((card, index) => (
              <div className="col-xxl-3 col-xl-6 col-md-6 col-12 d-flex" key={index}>
                <div
                  className="card w-100 border-0"
                  style={{
                    ...cardBaseStyle,
                    background: card.gradient,
                    minHeight: 112,
                  }}
                >
                  <div
                    className="card-body d-flex justify-content-between align-items-center"
                    style={{ padding: "22px 20px" }}
                  >
                    <div>
                      <h6
                        style={{
                          color: "rgba(255,255,255,0.92)",
                          marginBottom: 10,
                          fontSize: 15,
                          fontWeight: 500,
                        }}
                      >
                        {card.title}
                      </h6>
                      <h2
                        style={{
                          color: "#fff",
                          margin: 0,
                          fontWeight: 700,
                          fontSize: 24,
                          lineHeight: 1.1,
                        }}
                      >
                        {card.value}
                      </h2>
                    </div>
                    <div
                      style={{
                        width: 62,
                        height: 62,
                        borderRadius: 16,
                        background: "rgba(255,255,255,0.18)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 28,
                        flexShrink: 0,
                      }}
                    >
                      <i className={card.icon} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div
            className="card table-list-card no-search border-0 shadow-sm"
            style={cardBaseStyle}
          >
            <div
              className="card-header d-flex align-items-center justify-content-between flex-wrap"
              style={{
                padding: "22px 22px 16px",
                background: "#fff",
                borderBottom: "1px solid #eef1f4",
                gap: 12,
              }}
            >
              <div>
                <h4 className="mb-1">{t("Stock History Report")}</h4>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                  {t("Clean view of product movement summary")}
                </p>
              </div>

              <ul className="table-top-head" style={{ marginBottom: 0 }}>
                <TooltipIcons />
                <li>
                  <Link to="#" onClick={handleDownloadPdf}>
                    <i className="ti ti-printer" />
                  </Link>
                </li>
              </ul>
            </div>

            <div className="card-body" style={{ padding: "18px 22px 22px" }}>
              <div
                className="d-flex justify-content-between align-items-center flex-wrap"
                style={{
                  marginBottom: 14,
                  gap: 10,
                  paddingBottom: 10,
                  borderBottom: "1px solid #f1f3f5",
                }}
              >
                <div className="text-muted" style={{ fontSize: 15 }}>
                  {t("API Records")}: <strong>{Array.isArray(tableData) ? tableData.length : 0}</strong>
                </div>
                <div className="text-muted" style={{ fontSize: 15 }}>
                  {t("Total")}: <strong>{pagination?.total || 0}</strong>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={Array.isArray(tableData) ? tableData : []}
                  loading={loading}
                />
              </div>

              <div
                className="d-flex justify-content-between align-items-center mt-3 flex-wrap"
                style={{
                  gap: 10,
                  paddingTop: 12,
                  borderTop: "1px solid #f1f3f5",
                }}
              >
                <div className="text-dark" style={{ fontSize: 15 }}>
                  <strong>{t("Total")}:</strong> {pagination?.total || 0}
                </div>
                <div className="text-dark" style={{ fontSize: 15 }}>
                  <strong>{t("Page")}:</strong> {pagination?.page || 1} /{" "}
                  {pagination?.totalPages || 1}
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default Stockhistory;
